import React, { useCallback, useEffect, useState } from 'react';
import {
  ATMSegment,
  ATMHeader,
  ATMGrid,
  ATMField,
  ATMDivider,
  ATMSelect,
  ATMInput,
  ATMTextArea,
} from 'shared-it-appmod-ui';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import { useDistrictContext } from 'src/contexts/district.context';
import { useFlightTypeContext } from 'src/contexts/flight-type.context';
import { usePolygonContext } from 'src/contexts/polygon.context';
import { actionTypes } from 'src/ducks/flight-type.duck';
import { actionTypes as districtActionTypes } from 'src/ducks/district.duck';
import { actionTypes as polygonActionTypes } from 'src/ducks/polygon.duck';
import { getDistrictStatus } from 'src/selectors/district.selector';
import { getPolygonStatus } from 'src/selectors/polygon.selector';
import Lang from 'src/libraries/language';
import { DistrictType, FileCategory, SettingKey } from 'src/constants';
import { IRequestFormMethods } from 'src/models/request.model';
import { getFlightTypeStatus } from 'src/selectors/flight-type.selector';
import { useSensitiveCommunitiesContext } from 'src/contexts/sensitive-communities.context';
import { useDroneAreasContext } from 'src/contexts/drone-areas.context';
import { useSensitiveCitiesContext } from 'src/contexts/sensitive-cities.context';
import FormFlagUpload from 'src/components/organisms/form-flag-upload/form-flag-upload.component';
import FormFlag from 'src/components/molecules/form-flag/form-flag.component';
import FormFlagField from 'src/components/organisms/form-flag-field/form-flag-field.component';
import { useSetting } from 'src/hooks/setting.hook';
import FlightRequestCircuitForm from '../../flight-request-circuit/flight-request-circuit-form/flight-request-circuit-form.component';
import FlightRequestPatrolPassengerEquipment from '../flight-request-patrol-passenger-equipment/flight-request-patrol-passenger-equipment.component';
import FlightRequestPassengerInformationForm from '../../flight-request-passenger-information/flight-request-passenger-information-form/flight-request-passenger-information-form.component';
import styles from '../flight-request-patrol-passenger.module.scss';
import FlightRequestFlightArea from '../../flight-request-flight-area/flight-request-flight-area.component';
import FlightRequestContactInformationForm from '../../flight-request-contact-information/flight-request-contact-information-form/flight-request-contact-information-form.component';

type IProps = IRequestFormMethods & {
  isEdit?: boolean;
  control?: any;
};

const FlightRequestPatrolPassengerForm: React.FC<IProps> = ({
  control,
  errors,
  isEdit: _isEdit,
}) => {
  const { state, actions } = useFlightTypeContext();
  const { state: districtState, actions: districtActions } =
    useDistrictContext();
  const { state: polygonState, actions: polygonActions } = usePolygonContext();
  const passengerLimit = useSetting(SettingKey.MaxPassengers, 1);

  useEffect(() => {
    actions.listGET();
    districtActions.listGET();
    polygonActions.listGET();
  }, [actions, districtActions, polygonActions]);

  const status = getFlightTypeStatus(state, actionTypes.FLIGHT_TYPE_FETCH);
  const districtStatus = getDistrictStatus(
    districtState,
    districtActionTypes.DISTRICT_LIST_FETCH
  );
  const polygonStatus = getPolygonStatus(
    polygonState,
    polygonActionTypes.POLYGON_FETCH
  );
  const { state: sCommunitiesState, actions: sCommunitiesActions } = useSensitiveCommunitiesContext();
  const { state: droneAreasState, actions: droneAreasActions } = useDroneAreasContext();
  const { state: sCitiesState, actions: sCitiesActions } = useSensitiveCitiesContext();
  const [sCityPamLead, setSCityPamLead] = useState<string | null>('');
  const [sCommunityPamLead, setSCommunityPamLead] = useState<string | null>('');
  useEffect(() => {
    if (sCommunitiesActions && sCitiesActions && droneAreasActions) {
      sCommunitiesActions.listGET();
      sCitiesActions.listGET();
      droneAreasActions.listGET();
    }
  }, [sCommunitiesActions, sCitiesActions, droneAreasActions]);

  const isOldSCG =
    control?.defaultValuesRef?.current?.passengerFlight?.isOldSCG;

    const handleChange = useCallback(
      (value, type) => {
        if (type === 'city') {
          if (!value) {
            setSCityPamLead(null);
          } else {
            const sCity = sCitiesState.list.find((v) => v.sensitiveCity === value);
            setSCityPamLead(sCity.pamLead);
          }
          control.setValue('passengerFlight.sensitiveCity', value);
        } else if (type === 'drone') {
          control.setValue('passengerFlight.droneArea', value);
        } else {
          if (!value) {
            setSCommunityPamLead(null);
          } else {
            const sCommunity = sCommunitiesState.list.find((v) => v.sensitiveCommunity === value);
            setSCommunityPamLead(sCommunity.sensitiveCommunityPamLead);
          }
          control.setValue('passengerFlight.sensitiveCommunity', value);
        }
      },
      [state, control, sCitiesState, sCommunitiesState]
    );

  const [sensitiveCustomerFlag, setSensitiveCustomerFlag] = useState(control?.defaultValuesRef?.current?.passengerFlight?.sensitiveFlag || false);
  const defaultSCityPamLead = sCitiesState.list.find((v) => v.sensitiveCity === control?.defaultValuesRef?.current?.passengerFlight?.sensitiveCity)?.pamLead;
  const defaultSCommunityPamLead = sCommunitiesState.list.find((v) => v.sensitiveCommunity === control?.defaultValuesRef?.current?.passengerFlight?.sensitiveCommunity)?.sensitiveCommunityPamLead;

  return (
    <ATMSegment
      color="yellow"
      className={isOldSCG ? styles.wrapperOld : styles.wrapper}
    >
      <ATMHeader>
        {!isOldSCG
          ? Lang.TTL_REQUEST_PATROL_PASSENGER_CONTRACTOR
          : Lang.TTL_REQUEST_PATROL_PASSENGER}
      </ATMHeader>
      <ATMGrid columns={3}>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.flightType"
            label={<LabelRequired>{Lang.LBL_FLIGHT_TYPE}</LabelRequired>}
            as={ATMSelect}
            selectOnBlur={false}
            placeholder={Lang.LBL_SELECT}
            control={control}
            options={state.list.map((value) => ({
              key: value.flightTypeId,
              value: value.flightType,
              text: value.flightType,
            }))}
            search
            clearable
            error={errors.passengerFlight?.flightType}
            onChange={([_, { value }]) => value}
            loading={!state.list.length && status.fetching}
            disabled={!state.list.length && status.fetching}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.flightDept"
            defaultValue={
              control.defaultValuesRef.current?.request?.requestorDept ??
              control.defaultValuesRef.current?.passengerFlight?.flightDept
            }
            label={
              <LabelRequired>
                {Lang.LBL_FLIGHT_DISTRICT_DEPARTMENT}
              </LabelRequired>
            }
            as={ATMSelect}
            selectOnBlur={false}
            placeholder={Lang.LBL_SELECT}
            control={control}
            options={districtState.list
              .filter((value) => value.relationship === DistrictType.Company)
              .map((value) => ({
                key: value.key,
                value: value.value,
                text: value.value,
              }))}
            search
            error={errors.passengerFlight?.flightDept}
            onChange={([_, { value }]) => value}
            loading={!districtState.list.length && districtStatus.fetching}
            disabled={!districtState.list.length && districtStatus.fetching}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMGrid columns={3}>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.project"
            label={Lang.LBL_PROJECT}
            placeholder={Lang.LBL_ENTER}
            as={ATMInput}
            control={control}
            maxLength={50}
            error={errors.passengerFlight?.project}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.purpose"
            label={<LabelRequired>{Lang.LBL_PURPOSE}</LabelRequired>}
            placeholder={Lang.LBL_ENTER}
            as={ATMInput}
            control={control}
            maxLength={200}
            error={errors.passengerFlight?.purpose}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.polygon"
            label={Lang.LBL_Polygon}
            as={ATMSelect}
            selectOnBlur={false}
            placeholder={Lang.LBL_SELECT}
            control={control}
            options={polygonState.list.map((value) => ({
              key: value.polygonId,
              value: value.polygon,
              text: value.polygon,
            }))}
            search
            error={errors.passengerFlight?.polygon}
            onChange={([_, { value }]) => value}
            loading={!polygonState.list.length && polygonStatus.fetching}
            disabled={!polygonState.list.length && polygonStatus.fetching}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMGrid columns={2}>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.flightDestination"
            label={<LabelRequired>{Lang.LBL_FLIGHT_DESTINATION}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.passengerFlight?.flightDestination}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMGrid columns={2}>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.flightLocation"
            label={<LabelRequired>{Lang.LBL_PICKUP_LOCATION}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.passengerFlight?.flightLocation}
          />
        </ATMGrid.Column>
      </ATMGrid>
      <ATMGrid columns={2}>
        <ATMGrid.Column>
          <FlightRequestCircuitForm // This is the Pipeline information
            name="passengerFlight.circuitInfos"
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestPassengerInformationForm
        name="passengerFlight.passengerInfos"
        control={control}
        errors={errors}
        limit={passengerLimit}
      />

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="passengerFlight.nightFlag"
            label={Lang.LBL_NIGHT_FLIGHT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlagUpload
            name="passengerFlight.sensitiveFlag"
            listName="passengerFlight.sensitiveFiles"
            label={Lang.LBL_SENSITIVE_CUSTOMER}
            allowedTypes={[FileCategory.SensitiveCustomer]}
            control={control}
            errors={errors}
            setValue={setSensitiveCustomerFlag}
          />
        </ATMGrid.Column>
  {sensitiveCustomerFlag && (
  <ATMGrid columns={4}>
  <ATMGrid.Column>
        <ATMField
              name="passengerFlight.sensitiveCity"
              label={Lang.LBL_SENSITIVE_CITY}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCitiesState.list.map((item) => ({
                key: item.sensitiveCityId,
                value: item.sensitiveCity,
                text: item.sensitiveCity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'city');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.passengerFlight?.sensitiveCity}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
        />
        {!(sCityPamLead === null) && (sCityPamLead || defaultSCityPamLead) && <>
        <div>City PAM Lead:</div>
        <div>{sCityPamLead || defaultSCityPamLead}</div>
        </>}
 </ATMGrid.Column>

        <ATMGrid.Column>
<ATMField
              name="passengerFlight.sensitiveCommunity"
              label={Lang.LBL_SENSITIVE_COMMUNITIES}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCommunitiesState.list.map((item) => ({
                key: item.sensitiveCommunityId,
                value: item.sensitiveCommunity,
                text: item.sensitiveCommunity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'community');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.passengerFlight?.sensitiveCommunity}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
/>
{!(sCommunityPamLead === null) && (sCommunityPamLead || defaultSCommunityPamLead) && <>
        <div style={{ opacity: '80%' }}>Community PAM Lead:</div>
        <div>{sCommunityPamLead || defaultSCommunityPamLead}</div>
        </>}
 </ATMGrid.Column>

 <ATMGrid.Column>
        <ATMField
              name="passengerFlight.droneArea"
              label={Lang.LBL_DRONE_AREA}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={droneAreasState.list.map((item) => ({
                key: item.droneAreasId,
                value: item.SensitiveCityLocation,
                text: item.SensitiveCityLocation,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'drone');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.passengerFlight?.droneArea}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
              clearable
        />
 </ATMGrid.Column>
  </ATMGrid>)}
        <ATMGrid.Column>
          <FormFlagUpload
            name="passengerFlight.culturalFlag"
            listName="passengerFlight.culturalFiles"
            label={Lang.LBL_CULTURAL_RESTRICTION}
            allowedTypes={[FileCategory.EnvironmentalCulturalRestrictions]}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FlightRequestPatrolPassengerEquipment
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlag
            name="passengerFlight.helmetFlag"
            label={Lang.LBL_HELMENT_SUIT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlag
            name="passengerFlight.hazmatFlag"
            label={Lang.LBL_HAZMAT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlagField
            name="passengerFlight.basketFlag"
            label={Lang.LBL_BASKET_REQUIRED}
            control={control}
            errors={errors}
          >
            <ATMField
              name="passengerFlight.basketWeight"
              width={4}
              as={ATMInput}
              label={Lang.LBL_BASKET_WEIGHT}
              type="number"
              control={control}
              placeholder={Lang.LBL_ENTER}
              error={errors.passengerFlight?.basketWeight}
            />
          </FormFlagField>
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestContactInformationForm
        category="passengerFlight"
        control={control}
        errors={errors}
      />

      <FlightRequestFlightArea
        category="passengerFlight"
        control={control}
        errors={errors}
      />
      <ATMDivider />

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_ADDITIONAL_COMMENT}
      </ATMHeader>

      <ATMGrid columns={2}>
        <ATMGrid.Column>
          <ATMField
            name="passengerFlight.passengerComments"
            label={Lang.LBL_COMMENT}
            as={ATMTextArea}
            control={control}
            error={errors.passengerFlight?.passengerComments}
          />
        </ATMGrid.Column>
      </ATMGrid>
    </ATMSegment>
  );
};

export default FlightRequestPatrolPassengerForm;
